import React, { useEffect, useState } from 'react';
import { Container, Paper, Typography, Box, List, ListItem, ListItemText, Divider, Chip, Button } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDesign } from '../context/DesignContext';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

const formatMXN = (value: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config } = useDesign();
  const [order, setOrder] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.id || !orderId) {
      setLoading(false);
      return;
    }
    const ref = doc(db, 'users', user.id, 'orders', orderId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setError('El pedido no existe o no está disponible.');
          setOrder(null);
        } else {
          setOrder({ id: snap.id, ...snap.data() });
        }
        setLoading(false);
      },
      (err) => {
        console.warn('[OrderDetail] Error obteniendo pedido', err);
        setError('No se pudo cargar el pedido');
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user?.id, orderId]);

  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            borderRadius: 4,
            boxShadow: '0 8px 30px rgba(0,0,0,0.08)'
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>
            Inicia sesión para ver tus pedidos
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/profile')} 
            sx={{ 
              mt: 2,
              borderRadius: '50px',
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: 'primary.main',
              boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)'
            }}
          >
            Ir a Mi Perfil
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom 
        sx={{ 
          fontFamily: '"Playfair Display", serif', 
          fontWeight: 700, 
          color: '#1a1a1a',
          mb: 3
        }}
      >
        Detalle del Pedido
      </Typography>

      {loading && (
        <Typography variant="body2" sx={{ color: '#666' }}>Cargando pedido...</Typography>
      )}
      {error && (
        <Typography variant="body2" sx={{ color: 'error.main', mb: 2 }}>{error}</Typography>
      )}

      {order && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4,
            borderRadius: 4,
            boxShadow: '0 8px 30px rgba(0,0,0,0.08)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>
                Pedido #{order.id.slice(-6).toUpperCase()}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                ID Completo: {order.id}
              </Typography>
            </Box>
            <Chip 
              label={order.status === 'paid' ? 'Pagado' : order.status} 
              color={order.status === 'paid' ? 'success' : 'default'}
              sx={{ fontWeight: 600 }}
            />
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ color: '#666', fontSize: '1rem' }}>
              <strong>Fecha:</strong> {order.createdAt?.toDate ? order.createdAt.toDate().toLocaleString() : '—'}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
            <Box>
              <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, mb: 2 }}>
                Información de Envío
              </Typography>
              <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Destinatario</Typography>
                <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                  {order.shipping?.recipientName}
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Dirección</Typography>
                <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                  {order.shipping?.street}, Col. {order.shipping?.colonia}<br />
                  {order.shipping?.city}, {order.shipping?.state} {order.shipping?.zipCode}
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Entrega Programada</Typography>
                <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                  Fecha: {order.shipping?.deliveryDate}<br />
                  Hora: {order.shipping?.deliveryTime}
                </Typography>

                {order.shipping?.message && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Mensaje de regalo</Typography>
                    <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic' }}>
                      "{order.shipping?.message}"
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            <Box>
              <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, mb: 2 }}>
                Resumen de Pago
              </Typography>
              <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Método de Pago</Typography>
                <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                  {order.payment?.method === 'card' ? 'Tarjeta de Crédito/Débito' : order.payment?.method}
                  {order.payment?.cardLast4 && ` •••• ${order.payment.cardLast4}`}
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Datos del Remitente</Typography>
                <Typography variant="body2" sx={{ color: '#555' }}>
                  {order.sender?.name}<br />
                  {order.sender?.email}<br />
                  {order.sender?.phone}
                </Typography>
              </Box>
              <Button
                variant="text"
                startIcon={<WhatsAppIcon />}
                onClick={() => {
                  const message = `Hola, necesito ayuda con mi pedido #${order.id}`;
                  const phone = config.supportPhone || '522222222222';
                  window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
                }}
                sx={{
                  mt: 2,
                  width: '100%',
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#4caf50',
                  '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.08)' }
                }}
              >
                Ayuda con este pedido
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, mb: 2 }}>
            Productos
          </Typography>
          <List disablePadding>
            {(order.items || []).map((it: any, idx: number) => (
              <ListItem 
                key={idx} 
                divider={idx !== order.items.length - 1}
                sx={{ py: 2, px: 0 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  {it.image && (
                    <Box
                      component="img"
                      src={it.image}
                      alt={it.name}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 2,
                        mr: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                  )}
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {it.name} <Typography component="span" variant="body2" color="text.secondary">x{it.quantity}</Typography>
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {it.category}
                      </Typography>
                    }
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {formatMXN(it.price)}
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>

          <Box sx={{ mt: 3, p: 3, bgcolor: '#f9f9f9', borderRadius: 3, maxWidth: 400, ml: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Subtotal:</Typography>
              <Typography variant="body1">{formatMXN(order.subtotal || 0)}</Typography>
            </Box>
            {(order.shippingCost !== undefined && order.shippingCost !== null) && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Envío:</Typography>
                <Typography variant="body1">{formatMXN(Number(order.shippingCost) || 0)}</Typography>
              </Box>
            )}
            {order.discount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" color="success.main">Descuento:</Typography>
                <Typography variant="body1" color="success.main">-{formatMXN(order.discount)}</Typography>
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Total:</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {formatMXN(order.total || 0)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 4, flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/profile')}
              sx={{
                borderRadius: '50px',
                textTransform: 'none',
                fontWeight: 600,
                borderWidth: 2,
                '&:hover': { borderWidth: 2 }
              }}
            >
              Volver a Mis pedidos
            </Button>
            <Button 
              variant="contained" 
              onClick={() => navigate('/catalog')} 
              sx={{ 
                backgroundColor: 'primary.main', 
                '&:hover': { backgroundColor: 'primary.dark' },
                borderRadius: '50px',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)',
                px: 4
              }}
            >
              Seguir comprando
            </Button>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default OrderDetailPage;